import distance from "@turf/distance";
import { point as turfPoint } from "@turf/helpers";
import { App } from "antd";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { Coords, Point, TaskStartVisitPayload } from "tm-shared/types";

import { useAppDispatch } from "@/hooks/redux";
import { useMakeCommand } from "@/hooks/requestQueue/useRequestQueue";
import { BaseModal } from "@/shared/ui/BaseModal";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { setTaskCompleted } from "@/store/slices/work";
import { getPreciseLocation, parsePointCoords } from "@/utils/geo/geo";

import { StartVisitModalWarningPayload } from "../StartVisitModalWarning";

export interface StartVisitModalPayload {
    point: Point;
    taskId: string;
}

interface StartVisitModalProps {
    onCloseModal: () => void;
    payload: StartVisitModalPayload;
    onOpenWarningModal: (payload: StartVisitModalWarningPayload) => void;
}

export const StartVisitModal: FC<StartVisitModalProps> = ({
    payload,
    onCloseModal,
    onOpenWarningModal,
}) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const cancelledRef = useRef(false);

    const { makeTaskCompleteCommands } = useMakeCommand();
    const { point, taskId } = payload;

    const pointCoords = useMemo(
        () => parsePointCoords(point.market.building.geo ?? null),
        [point.market.building.geo],
    );

    const handleTaskComplete = useCallback(
        async (coords: Coords | null = null) => {
            const payload: TaskStartVisitPayload = { coords };

            const taskCompleteCommands = await makeTaskCompleteCommands({
                params: {
                    taskId,
                    hasPhoto: false,
                    payload,
                },
                coords,
            });

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: {
                        pointId: point.id,
                        taskId,
                    },
                    commands: taskCompleteCommands,
                }),
            );

            dispatch(
                setTaskCompleted({
                    targetId: point.id,
                    taskId,
                }),
            );

            onCloseModal();
        },
        [dispatch, makeTaskCompleteCommands, point.id, onCloseModal, taskId],
    );

    const handleConfirm = async () => {
        cancelledRef.current = false;
        setLoading(true);

        try {
            const coords = await getPreciseLocation();

            if (cancelledRef.current) return;

            if (pointCoords) {
                const myPoint = turfPoint([coords.lng, coords.lat]);
                const marketPoint = turfPoint(pointCoords);

                const dist = distance(myPoint, marketPoint, {
                    units: "kilometers",
                });
                const distRounded = Math.round(dist * 10) / 10;

                if (distRounded > 10) {
                    onOpenWarningModal({
                        ...payload,
                        dist: distRounded,
                        coords,
                    });

                    return;
                }
            }

            await handleTaskComplete(coords);
        } catch (error) {
            if (cancelledRef.current) return;

            const geoErr = error as GeolocationPositionError;
            if (geoErr && typeof geoErr === "object" && "code" in geoErr) {
                if (geoErr.code === 1) {
                    notification.error({
                        title: "Не вдалося отримати вашу геолокацію. Дозвольте доступ та спробуйте ще раз.",
                    });

                    return;
                }
            }

            await handleTaskComplete();
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        cancelledRef.current = true;
        onCloseModal();
    };

    return (
        <BaseModal
            open
            onClose={handleClose}
            onConfirm={handleConfirm}
            title="Розпочати візит"
            description="Після підтвердження початку роботи у вибраній торговій точці розпочнеться відлік робочого часу."
            confirmText="Розпочати"
            cancelText="Скасувати"
            confirmLoading={loading}
            mask={{ closable: !loading }}
        />
    );
};
